// SiteScopeBadge — persistent chip in the top area of the tenant portal
// so a site-scoped supervisor always knows their view is narrowed.
// Sourced from the authed user's `site_scope` + `assigned_agent_ids` —
// no extra API round-trip needed.
import React from 'react';
import { ShieldCheck, Users } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

export const SiteScopeBadge = () => {
  const { user } = useAuth();
  if (!user) return null;

  const sites = Array.isArray(user.site_scope) ? user.site_scope.filter(Boolean) : [];
  const assigned = Array.isArray(user.assigned_agent_ids)
    ? user.assigned_agent_ids.filter(Boolean)
    : [];

  // Show only when something actually narrows the view.
  if (sites.length === 0 && assigned.length === 0) return null;

  // Colour resolution — TotalFusion spec (Feb 24 2026 mockup): pill
  // is BLACK background with GOLD writing — the "reverse" treatment
  // that mirrors the sidebar's active nav item. Uses the tenant's
  // `--brand-primary` (heavy fill, black) as bg and `--brand-accent`
  // (accent, gold) as writing so the two chrome surfaces (pill + active
  // nav) stay visually coherent. Falls through the CSS-var chain to
  // the amber tokens for any tenant that hasn't set `--brand-primary`
  // (ipvsyabbit and other non-configured tenants render byte-identical
  // to pre-theming).
  const scopedStyle = {
    backgroundColor: 'var(--brand-primary, var(--brand-badge-bg, rgb(255 251 235)))',
    borderColor:    'var(--brand-primary, var(--brand-badge-border, rgb(253 230 138)))',
    color:          'var(--brand-accent, var(--brand-badge-text, rgb(146 64 14)))',
  };
  const iconStyle = { color: 'var(--brand-accent, var(--brand-badge-text, rgb(217 119 6)))' };

  if (sites.length > 0) {
    return (
      <div
        className="mx-6 md:mx-8 mt-4 -mb-2 inline-flex items-center gap-2 text-xs font-semibold rounded-full px-3 py-1 border"
        style={scopedStyle}
        data-testid="site-scope-badge"
        title={`You are viewing data for ${sites.length === 1 ? 'this site' : 'these sites'} only. Other sites' data is hidden.`}
      >
        <ShieldCheck className="w-3.5 h-3.5" style={iconStyle} />
        <span className="text-[10px] uppercase tracking-wider" style={iconStyle}>Scope</span>
        <span data-testid="site-scope-badge-sites">{sites.join(' · ')}</span>
      </div>
    );
  }

  return (
    <div
      className="mx-6 md:mx-8 mt-4 -mb-2 inline-flex items-center gap-2 text-xs font-semibold rounded-full px-3 py-1 border"
      style={scopedStyle}
      data-testid="site-scope-badge"
      title={`You are viewing data for ${assigned.length} assigned agent(s) only.`}
    >
      <Users className="w-3.5 h-3.5" style={iconStyle} />
      <span className="text-[10px] uppercase tracking-wider" style={iconStyle}>Scope</span>
      <span data-testid="site-scope-badge-assigned">{assigned.length} agent{assigned.length === 1 ? '' : 's'}</span>
    </div>
  );
};

export default SiteScopeBadge;
